import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, AlertTriangle, Sparkles, ChevronRight, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';

const EntityCard = ({ tone, label, entity }) => {
  const palette = tone === 'good'
    ? { bg: 'bg-green-50', border: 'border-green-200', heading: 'text-green-800', sub: 'text-green-700', value: 'text-green-800', muted: 'text-green-600', icon: <CheckCircle2 className="h-5 w-5 text-green-600" /> }
    : { bg: 'bg-red-50', border: 'border-red-200', heading: 'text-red-800', sub: 'text-red-700', value: 'text-red-800', muted: 'text-red-600', icon: <AlertTriangle className="h-5 w-5 text-red-600" /> };

  return (
    <div className={`${palette.bg} rounded-xl p-4 border ${palette.border}`}>
      <div className="flex items-center gap-2 mb-3">
        {palette.icon}
        <span className={`text-sm font-medium ${palette.sub}`}>{label}</span>
      </div>
      <h3 className={`text-lg font-bold ${palette.heading}`}>{entity.name}</h3>
      <p className={`text-sm ${palette.muted} mb-4`}>{entity.location || entity.department}</p>
      <div className="space-y-3">
        <div className="flex justify-between">
          <span className={`text-sm ${palette.sub}`}>Premium Rate</span>
          <span className={`text-lg font-bold ${palette.value}`}>{entity.premium_percent}%</span>
        </div>
        <div className="flex justify-between">
          <span className={`text-sm ${palette.sub}`}>Team Size</span>
          <span className={`font-medium ${palette.value}`}>{entity.employee_count} employees</span>
        </div>
        <div className="flex justify-between">
          <span className={`text-sm ${palette.sub}`}>Total Hours</span>
          <span className={`font-medium ${palette.value}`}>{entity.total_hours.toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span className={`text-sm ${palette.sub}`}>Premium Hours</span>
          <span className={`font-medium ${palette.value}`}>{entity.premium_hours.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

export const ComparisonDetailModal = ({ comparison, onClose, onViewInDashboard }) => {
  return (
    <AnimatePresence>
      {comparison && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2E5C8A] text-white p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold">Supervisor Comparison</h2>
                  <p className="text-white/80 mt-1">{comparison.entity_a.department}</p>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-6 mb-6">
                <EntityCard tone="good" label="Lower Premium" entity={comparison.entity_a} />
                <EntityCard tone="bad" label="Higher Premium" entity={comparison.entity_b} />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-6 w-6 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-800">{comparison.variance} percentage point difference</h4>
                    <p className="text-sm text-amber-700 mt-1">{comparison.insight}</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <h4 className="font-semibold text-slate-800 mb-3">Questions to Investigate</h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <ChevronRight className="h-4 w-4 text-[#4CAF93] mt-0.5 flex-shrink-0" />
                    How does {comparison.entity_a.name} schedule shifts differently?
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="h-4 w-4 text-[#4CAF93] mt-0.5 flex-shrink-0" />
                    Are there cross-training opportunities to reduce overtime reliance?
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="h-4 w-4 text-[#4CAF93] mt-0.5 flex-shrink-0" />
                    What's the staffing pattern during peak hours?
                  </li>
                </ul>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => onViewInDashboard(comparison)} className="flex-1 bg-[#2E5C8A] hover:bg-[#1e3a5f]">
                  <Building2 className="h-4 w-4 mr-2" />
                  View Department in Dashboard
                </Button>
                <Button variant="outline" onClick={onClose}>Close</Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
